/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  ICON_ADD_CIRCLE_OUTLINE,
  Row,
  SdsIcon,
  SequenceList,
  SequenceListProps,
  TextField,
  TEXTFIELD_TYPE_TEXTAREA,
} from '@squiz/sds';
import { AI_STRINGS } from 'apps/artificial-intelligence/src/Constants/Strings.constants';
import React, { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useSemanticAuditorTestBuilderStore } from '../../SemanticAuditorTestBuilder.state';
import { TestCase } from './SemanticAuditorTestQuestion.defs';

export const SemanticAuditorTestQuestions = (): JSX.Element => {
  const id = useId();
  const { questions, setQuestions, questionsErrors } = useSemanticAuditorTestBuilderStore();
  const { t } = useTranslation();

  const labels = {
    addQuestion: t(AI_STRINGS.addQuestion),
    question: t(AI_STRINGS.question),
    questions: t(AI_STRINGS.questions),
    referenceResponse: t(AI_STRINGS.referenceResponse),
    requiredField: t(AI_STRINGS.requiredField),
    writeQuestionToAskChatbot: t(AI_STRINGS.writeQuestionToAskChatbot),
    writeResponseToCompare: t(AI_STRINGS.writeResponseToCompare),
    writeTheQuestionsYouWant: t(AI_STRINGS.writeTheQuestionsYouWant),
  };

  const handleQuestionListChange = useCallback(
    (index: number, field: string, value: string): void => {
      const newQuestions = [...questions];
      newQuestions[index] = { ...newQuestions[index], [field]: value };
      setQuestions(newQuestions);
    },
    [questions, setQuestions],
  );
  const sequenceListProps: SequenceListProps<TestCase> = {
    addButtonContent: (
      <>
        <SdsIcon svg={ICON_ADD_CIRCLE_OUTLINE} />
        {labels.addQuestion}
      </>
    ),
    classes: 'semantic-auditor-test-questions__list',
    listItemContent: (i) => (
      <div className="semantic-auditor-test-questions__list-item-content">
        <TextField
          classes="semantic-auditor-test-questions__question"
          errorMessage={questionsErrors[i]?.question ? labels.requiredField : undefined}
          fieldLabel={labels.question}
          id={`question-${id}-${i}`}
          name={labels.question}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuestionListChange(i, 'question', e.target.value)}
          placeholder={labels.writeQuestionToAskChatbot}
          required
          value={questions[i].question || ''}
        />
        <TextField
          classes="semantic-auditor-test-questions__response"
          fieldLabel={labels.referenceResponse}
          id={`response-${id}-${i}`}
          name={labels.referenceResponse}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleQuestionListChange(i, 'referenceResponse', e.target.value)
          }
          placeholder={labels.writeResponseToCompare}
          type={TEXTFIELD_TYPE_TEXTAREA}
          value={questions[i].referenceResponse || ''}
        />
      </div>
    ),
    listState: questions,
    listStateHandler: setQuestions,
    max: 20,
    min: 1,
  };
  return (
    <Row
      border={false}
      classes="semantic-auditor-test-questions"
      subtitle={labels.writeTheQuestionsYouWant}
      title={labels.questions}
    >
      <SequenceList {...sequenceListProps} />
    </Row>
  );
};
